import React, { useState, useEffect, useCallback, useRef } from "react";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MultiSelect } from "@/components/MultiSelect/MultiSelect";
import { fetchVenues } from "@/modules/eventRegistration/features/EventVenueManagement/api/fetchVenues/fetchVenuesApi";
import { loadAllFilterOptions } from "../../utils/loadAllFilterOptions";
import type { TrainingHistoryReportFilters } from "../../hooks/useTrainingHistoryReport";

interface TrainingHistoryReportFiltersProps {
  filters: TrainingHistoryReportFilters;
  isExporting: boolean;
  onFilterChange: (
    key: keyof TrainingHistoryReportFilters,
    value: string | number[] | undefined,
  ) => void;
  onExportCsv: () => void;
}

const TrainingHistoryReportFilters: React.FC<
  TrainingHistoryReportFiltersProps
> = ({ filters, isExporting, onFilterChange, onExportCsv }) => {
  const [searchTerm, setSearchTerm] = useState(filters.searchTerm ?? "");
  const [locationIds, setLocationIds] = useState<string[]>(
    (filters.locationIds ?? []).map(String),
  );
  const [sessionDateFrom, setSessionDateFrom] = useState(
    filters.sessionDateFrom ?? "",
  );
  const [sessionDateTo, setSessionDateTo] = useState(
    filters.sessionDateTo ?? "",
  );
  const [locationOptions, setLocationOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);
  const isMounted = useRef(false);

  useEffect(() => {
    loadAllFilterOptions((page, pageSize) =>
      fetchVenues({ page, pageSize, sortBy: "name", sortOrder: "ASC" }).then(
        (response) => ({
          options: response.data.map((venue) => ({
            label: venue.name,
            value: String(venue.id),
          })),
          totalCount: response.meta.totalCount,
        }),
      ),
    ).then(setLocationOptions);
  }, []);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    onFilterChange("searchTerm", debouncedSearchTerm || undefined);
  }, [debouncedSearchTerm, onFilterChange]);

  const handleLocationChange = (value: string[]) => {
    setLocationIds(value);
    onFilterChange("locationIds", value.length ? value.map(Number) : undefined);
  };

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSessionDateFrom(e.target.value);
    onFilterChange("sessionDateFrom", e.target.value || undefined);
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSessionDateTo(e.target.value);
    onFilterChange("sessionDateTo", e.target.value || undefined);
  };

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setLocationIds([]);
    setSessionDateFrom("");
    setSessionDateTo("");
    onFilterChange("searchTerm", undefined);
    onFilterChange("locationIds", undefined);
    onFilterChange("sessionDateFrom", undefined);
    onFilterChange("sessionDateTo", undefined);
  }, [onFilterChange]);

  const hasActiveFilters =
    searchTerm || locationIds.length || sessionDateFrom || sessionDateTo;

  return (
    <div className="mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <div>
          <Label htmlFor="searchTerm">Search</Label>
          <Input
            className="mt-1.5 bg-white"
            id="searchTerm"
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Name, company, event..."
            type="text"
            value={searchTerm}
          />
        </div>

        <div>
          <Label>Location</Label>
          <MultiSelect
            className="mt-1.5"
            onChange={handleLocationChange}
            options={locationOptions}
            placeholder="All Locations"
            value={locationIds}
          />
        </div>

        <div>
          <Label htmlFor="sessionDateFrom">Session Date From</Label>
          <Input
            className="mt-1.5 bg-white"
            id="sessionDateFrom"
            onChange={handleDateFromChange}
            type="date"
            value={sessionDateFrom}
          />
        </div>

        <div>
          <Label htmlFor="sessionDateTo">Session Date To</Label>
          <Input
            className="mt-1.5 bg-white"
            id="sessionDateTo"
            onChange={handleDateToChange}
            type="date"
            value={sessionDateTo}
          />
        </div>

        <div className="flex items-end gap-2">
          <Button
            className="border-gray-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            disabled={!hasActiveFilters}
            onClick={handleClearFilters}
            variant="outline"
          >
            Clear Filters
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700"
            disabled={isExporting}
            onClick={onExportCsv}
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {isExporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TrainingHistoryReportFilters;
